import './styles.scss';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { ClickAwayListener } from '@mui/base';
import { useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { IconButton } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import l from '../../../../lang';
import { Urls } from '../../../../config/urls';
import { getLanguage, openNewTabAndFocus } from '../../../../utils';
import { ReactComponent as DeclinedWarningIcon } from '../../../../assets/icons/warning-alert.svg';
import { ReactComponent as DeclinedStatusIcon } from '../../../../assets/icons/client-transaction-declined.svg';

interface TransactionTooltipButtonProps {
  type: string;
  transactionId: string;
  email: string;
  document: string;
  documentType: string;
}

type TransactionTooltipProps = TransactionTooltipButtonProps & TooltipProps;

const getHelpUrl = () => {
  const lang = getLanguage();
  switch (lang) {
    case 'es':
      return Urls.checkoutLogsHelp.es;
    case 'en':
      return Urls.checkoutLogsHelp.en;
    case 'pt':
      return Urls.checkoutLogsHelp.pt;
    default:
      return Urls.checkoutLogsHelp.en;
  }
};

const openHelp = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.stopPropagation();
  openNewTabAndFocus(getHelpUrl());
};

const TransactionTooltip = styled(
  ({ type, transactionId = '-', document, documentType, email, className, ...props }: TransactionTooltipProps) => (
    <Tooltip
      /* @ts-ignore-next-line */
      title={
        <div className="transactionTooltip">
          <div className="transactionTooltip__header">
            <DeclinedWarningIcon className="transactionTooltip__header-icon" aria-hidden />
            <h3 className="transactionTooltip__title">{l('checkout-logs.tooltip.title')}:</h3>
          </div>

          <div className="transactionTooltip__fields">
            <div className="transactionTooltip__field">
              <span className="transactionTooltip__label">{l('checkout-logs.tooltip.transaction_id')}</span>
              <span className="transactionTooltip__value">{transactionId || '-'}</span>
            </div>
            <div className="transactionTooltip__field">
              <span className="transactionTooltip__label">{l('checkout-logs.tooltip.document')}</span>
              <span className="transactionTooltip__value">
                {documentType ? `(${documentType}) ` : ''}
                {document || '-'}
              </span>
            </div>
            <div className="transactionTooltip__field">
              <span className="transactionTooltip__label">{l('checkout-logs.tooltip.email')}</span>
              <span className="transactionTooltip__value">{email || '-'}</span>
            </div>
          </div>

          <div className="transactionTooltip__separator" />

          <div className="transactionTooltip__status">
            <span className="transactionTooltip__label">{l('checkout-logs.tooltip.status')}:</span>
            <span className="transactionTooltip__status-badge">
              <DeclinedStatusIcon className="transactionTooltip__status-icon" aria-hidden />
              <span>{l('transactions.status.DECLINED')}</span>
            </span>
          </div>

          <div className="transactionTooltip__attention">
            <span className="transactionTooltip__attention-title">{l('checkout-logs.details.review.title')}:</span>
            <span className="transactionTooltip__attention-desc">{l(`checkout-logs.type.${type}`)}</span>
          </div>

          <a className="transactionTooltip__help" onClick={openHelp}>
            {l('checkout-logs.tooltip.help')}
          </a>
        </div>
      }
      {...props}
      classes={{ popper: className }}
    />
  )
)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#ffffff',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 300,
    padding: 0,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dfe3e8',
    borderRadius: '8px',
    boxShadow: '2px 4px 12px rgba(0, 0, 0, 0.08)',
  },
}));

export const TransactionTooltipButton = ({
  transactionId,
  type,
  document,
  documentType,
  email,
}: TransactionTooltipButtonProps) => {
  const [open, setOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const onClickAway = (e: MouseEvent | TouchEvent) => {
    if (tooltipRef.current?.contains(e.target as Node)) {
      return;
    }

    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div>
        {/* @ts-ignore-next-line  */}
        <TransactionTooltip
          ref={tooltipRef}
          type={type}
          transactionId={transactionId}
          open={open}
          document={document}
          documentType={documentType}
          email={email}
        >
          <IconButton onClick={() => setOpen(!open)}>
            <InfoOutlined fontSize="small" />
          </IconButton>
        </TransactionTooltip>
      </div>
    </ClickAwayListener>
  );
};
